import React, { useState } from 'react';
import { features } from '../../components/data/data/Industries/IndustriesStaticData';
import { imagePath } from '../imagePath';
import { Link } from 'react-router-dom';
import ImageShimmer from '../../components/ShimmerEffects/ImageShimmer';
export const IndustriesImagesCard = ({
  title,
  subtitle,
  description,
  image,
  buttonText,
  mobileImage,
  onButtonClick,
  showButton = true,
  showTopLabel = true,
  showBottomOverlay = true,
  className = '',
  topLabelClassName = '',
  bottomOverlayClassName = '',
  buttonClassName = '',
}) => {
  // Add state for image loading
  const [isDesktopImageLoaded, setIsDesktopImageLoaded] = useState(false);
  const [isMobileImageLoaded, setIsMobileImageLoaded] = useState(false);

  return (
    <div className={`w-full ${className}`}>
      {/* Desktop/Tablet Image Wrapper */}
      <div className="hidden md:block relative w-full aspect-[16/9] overflow-hidden rounded-xl">
        {/* Shimmer Effect for Desktop - Add this */}
        {!isDesktopImageLoaded && (
          <ImageShimmer
            className="absolute inset-0 z-0"
            aspectRatio="16/9"
            rounded="rounded-xl"
          />
        )}

        {/* Top Label - visible on tablet/desktop */}
        {showTopLabel && (title || subtitle) && (
          <div
            className={`
              absolute z-10
              md:top-8 md:left-8
              lg:top-10 lg:left-12
              max-w-[65%] lg:max-w-[60%] xl:max-w-[550px]
              ${topLabelClassName}
            `}
          >
            {title && (
              <h2 className="text-[#FFFFFF] font-bold leading-tight text-xl md:text-3xl lg:text-4xl xl:text-5xl">
                {title}
              </h2>
            )}
            {subtitle && (
              <h3 className="text-[#FFFFFF] font-bold leading-tight md:text-3xl lg:text-4xl xl:text-5xl mt-1">
                {subtitle}
              </h3>
            )}
          </div>
        )}

        {/* Desktop Image - Add onLoad handler and transition */}
        <img
          src={image}
          alt={title || ''}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            isDesktopImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsDesktopImageLoaded(true)}
        />

        {/* Bottom Overlay Text - visible on tablet/desktop */}
        {showBottomOverlay && description && (
          <div
            className={`
              absolute z-10
              md:bottom-6 md:left-8
              lg:bottom-6 lg:left-12
              bg-[#121316] backdrop-blur-sm
              rounded-lg
              px-5 py-3
              max-w-[45%] lg:max-w-md
              ${bottomOverlayClassName}
            `}
          >
            <p className="text-white md:text-[18px] lg:text-[20px] leading-relaxed">
              {description}
            </p>
          </div>
        )}

        {/* Overlay Button - visible on tablet and desktop */}
        {showButton && buttonText && (
          <div className="absolute z-20 bottom-4 right-4 md:bottom-6 md:right-6 lg:bottom-6 lg:right-10">
            <button
              onClick={onButtonClick}
              className={`
                bg-[#EE2B2A]
                hover:bg-[#d92625]
                text-white font-semibold
                text-xs md:text-sm lg:text-base
                px-4 py-2 md:px-6 md:py-2.5 lg:px-8 lg:py-3
                rounded-lg
                transition
                whitespace-nowrap
                ${buttonClassName}
              `}
            >
              {buttonText}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Image Wrapper - Full Width, No Padding */}
      <div className="md:hidden relative w-screen -mx-[calc((100vw-100%)/2)] aspect-[16/9] overflow-hidden">
        {/* Shimmer Effect for Mobile - Add this */}
        {!isMobileImageLoaded && (
          <ImageShimmer
            className="absolute inset-0 z-0"
            aspectRatio="16/9"
            rounded="rounded-none"
          />
        )}

        {/* Mobile Image - Add onLoad handler and transition */}
        <img
          src={mobileImage || image}
          alt={title || ''}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            isMobileImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsMobileImageLoaded(true)}
        />
      </div>

      {/* Mobile Content (below image) - only visible on mobile */}
      <div className="md:hidden mt-5 space-y-3 ">
        {/* Mobile Title */}
        {(title || subtitle) && (
          <div>
            {title && (
              <h2 className="text-white font-bold leading-tight text-2xl md:text-lg">
                {title}
              </h2>
            )}
            {subtitle && (
              <h3 className="text-white font-bold leading-tight text-2xl md:text-lg mt-0.5">
                {subtitle}
              </h3>
            )}
          </div>
        )}

        {/* Mobile Description */}
        {description && (
          <p className="text-gray-300 text-lg leading-relaxed">{description}</p>
        )}

        {/* Mobile Button */}
        {showButton && buttonText && (
          <div className="flex justify-start">
            <button
              onClick={onButtonClick}
              className={`
                bg-[#EE2B2A]
                hover:bg-[#d92625]
                text-white font-semibold  
                text-sm
                px-6 py-2.5
                rounded-lg
                transition
                w-full
                ${buttonClassName}
              `}
            >
              {buttonText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const RetailECommerceFeatureCard = ({
  image,
  title,
  description,
  to,
  onClick,
  className = '',
}) => {
  const content = (
    <div
      className={`
        bg-[#31333A] 
        rounded-[20px]
        overflow-hidden
        w-full 
        min-h-[260px] sm:min-h-[300px] md:min-h-[340px] lg:min-h-[360px]
        shadow-[0_3px_6px_rgba(0,0,0,0.65)]
        ${className}
      `}
    >
      {/* Image */}
      <div className="w-full h-48 flex items-center justify-center overflow-hidden bg-[#2A2C32]">
        <img src={image} alt={title} className="w-full h-full object-cover" />
      </div>

      {/* Content */}
      <div className="px-4 py-4 text-left">
        <h3 className="text-white text-base font-semibold mb-2  transition-colors">
          {title}
        </h3>

        <p className="text-[#D8D8D8] text-sm font-normal leading-relaxed line-clamp-3 sm:line-clamp-4">
          {description}
        </p>
      </div>
    </div>
  );

  // If onClick is provided, use button. Otherwise use Link
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="block w-full text-left cursor-pointer"
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={to} className="block group">
      {content}
    </Link>
  );
};

export const ImageWithFeatureList = ({
  image,
  mobileFullImage,
  imageTitle,
  features = [],
  titleClass = '',
}) => {
  return (
    <section className="w-full overflow-x-hidden">
      {/* ================= MOBILE VIEW ================= */}
      <div className="block lg:hidden space-y-4">
        {/* TITLE */}
        {imageTitle && (
          <h2 className="text-white text-xl font-bold leading-tight px-4">
            {Array.isArray(imageTitle)
              ? imageTitle.map((line, idx) => (
                  <span key={idx} className="block">
                    {line}
                  </span>
                ))
              : imageTitle}
          </h2>
        )}

        {/* FULL WIDTH IMAGE */}
        {mobileFullImage && (
          <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">
            <img
              src={mobileFullImage}
              alt="feature-full"
              className="w-screen object-cover"
            />
          </div>
        )}

        {/* FEATURE LIST */}
        <div className="flex flex-col gap-4 px-4">
          {features.map((item, index) => (
            <div key={index} className="flex gap-3 items-start">
              {item.icon && (
                <img
                  src={item.icon}
                  alt=""
                  className="w-6 h-6 mt-1 flex-shrink-0"
                />
              )}

              <div>
                <h3 className="text-[#C7C7C7] text-base font-semibold mb-1">
                  {item.title}
                </h3>
                <p className="text-[#B3B3B3] text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ================= DESKTOP / TABLET VIEW ================= */}
      <div className="hidden lg:grid grid-cols-2 gap-16 items-center">
        {/* Image Section */}
        <div className="relative z-[999]">
          <img
            src={image}
            alt="feature"
            className="w-full object-contain rounded-xl clip-top-right"
          />

          {imageTitle && (
            <h2
              className={`
    absolute
    top-4 left-24
    md:top-8 md:left-20
    lg:top-5 lg:left-64
    xl:top-12 xl:left-64
    text-white
    text-sm sm:text-lg md:text-2xl lg:text-2xl xl:text-3xl
    font-bold
    leading-tight
    max-w-[380px]
    ${titleClass}
  `}
            >
              {Array.isArray(imageTitle)
                ? imageTitle.map((line, idx) => (
                    <span key={idx} className="block">
                      {line}
                    </span>
                  ))
                : imageTitle}
            </h2>
          )}
        </div>

        {/* Feature List */}
        <div className="flex flex-col divide-y divide-gray-300">
          {features.map((item, index) => (
            <div key={index} className="py-3">
              <h3 className="text-[#C7C7C7] text-xl font-semibold mb-2">
                {item.title}
              </h3>
              <p className="text-[#B3B3B3] text-base leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CaseStudyCard = ({
  title,
  description,
  image,
  ctaText = 'See the full use case',
  to,
}) => {
  return (
    <div
      className="
        w-full
        bg-[#35373A]
        rounded-2xl
        border-[3px] border-[#5F5F5F]
        p-4 md:p-6
      "
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        {/* Left Content */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <h3 className="text-white text-xl md:text-2xl font-semibold">
              {title}
            </h3>

            <p className="text-[#C7C7C7] text-[18px] md:text-base leading-relaxed">
              {description}
            </p>
          </div>

          {/* CTA LINK */}
          <Link
            to={to}
            className="
              text-[#EE2B2A]
              text-[18px] md:text-base
              font-medium
              flex items-center gap-2
              hover:underline
            "
          >
            {ctaText}
            <span>→</span>
          </Link>
        </div>

        {/* Right Image */}
        <div className="flex justify-center items-center relative left-0 md:left-12">
          <img
            src={image}
            alt={title}
            className="
              w-full
              max-w-md
              md:h-[240px]
              lg:h-[260px]
              object-contain
              rounded-xl
            "
          />
        </div>
      </div>
    </div>
  );
};

export const HeroBackground = ({ height = '600px' }) => (
  <div
    className="absolute top-0 left-0 right-0 bg-gradient-to-br from-green-800 to-green-900 rounded-b-[80px] -z-10"
    style={{ height }}
  ></div>
);

export const HeroSection = ({
  logo,
  title,
  subtitle,
  qrCode,
  videoThumbnail,
}) => (
  <div className="relative">
    {/* Main content container with rounded background */}
    <div className="relative  overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mt-12">
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            {logo && (
              <div className="bg-red-600 p-4 rounded">
                <img src={logo} alt="Brand Logo" className="h-12" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-[#FFFFFF] font-jakarta">
                {title}
              </h1>
              <p className="text-[#D8D8D8] font-jakarta mt-2">{subtitle}</p>
            </div>
          </div>
          {qrCode && (
            <div className="bg-white p-3 rounded">
              <img src={qrCode} alt="QR Code" className="w-16 h-16" />
            </div>
          )}
        </div>

        <div className="relative aspect-video bg-[#31333A] rounded-lg overflow-hidden flex items-center justify-center">
          {videoThumbnail ? (
            <img
              src={videoThumbnail}
              alt="Video"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-20 h-20  flex items-center justify-center cursor-pointer ">
              <div className="w-0 h-0 border-l-[20px] border-l-white border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1"></div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Curved bottom overlay effect */}
    {/* <div className="absolute bottom-0 left-0 right-0 h-32 bg-gray-900"></div> */}
  </div>
);

// Content Section Component
export const ContentSection = ({
  title,
  children,
  bgColor = 'bg-gray-900',
}) => (
  <div className={`${bgColor} mt-12  `}>
    <div className="max-w-7xl mx-auto px-6">
      <h2 className="text-2xl font-bold mb-6 font-jakarta text-[#FFFFFF]">
        {title}
      </h2>
      <div className="text-[#C8C8C8] leading-relaxed space-y-4">{children}</div>
    </div>
  </div>
);

// Image Grid Component
export const ImageGrid = ({ images, columns = 2 }) => (
  <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-20 mt-12`}>
    {images.map((image, index) => (
      <div key={index} className="flex flex-col items-center gap-3">
        {/* Image Box */}
        <div className="relative aspect-video bg-[#31333A] rounded-lg overflow-hidden w-full">
          {image.src ? (
            <img
              src={image.src}
              alt={image.alt || `Image ${index + 1}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              {image.placeholder || 'Image placeholder'}
            </div>
          )}
        </div>

        {/* Caption (outside & centered) */}
        {image.caption && (
          <p className="text-sm text-white text-center">{image.caption}</p>
        )}
      </div>
    ))}
  </div>
);

export const SpatialComputingContent = () => {
  return (
    <div className="relative w-full max-w-7xl mx-auto flex flex-col gap-6 md:gap-10 lg:gap-12 md:top-5">
      {/* Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 lg:gap-12 items-start">
        {/* Left Text */}
        <div className="w-full max-w-[580px]">
          {/* Hide description on mobile */}
          <p className="hidden md:block text-sm lg:text-lg font-normal text-[#DDDDDD] leading-relaxed mb-5 lg:mb-6">
            Spatial computing refers to the concept of computers understanding
            and interacting with the three-dimensional physical world, blending
            digital content seamlessly into real spaces instead of confining it
            to flat screens.
          </p>

          <h2 className="text-2xl md:text-2xl lg:text-5xl text-white font-light z-30 mb-2 md:mb-0">
            Features of
            <br />
            <span className="font-light">Spatial Computing</span>
          </h2>

          {/* Mobile subtitle */}
          <p className="md:hidden text-sm text-[#8E8E8E] mt-2 font-light">
            Purpose-built systems for the next generation of immersive
            technology.
          </p>
        </div>

        {/* Right Floating Badge */}
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 lg:gap-16">
        {features.map((item) => (
          <div
            key={item.title}
            className="bg-[#212121] rounded-[5px] p-4 md:p-5 lg:p-6 flex md:block gap-4"
          >
            {/* Icon */}
            <div className="w-12 h-12 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0 md:mb-3 lg:mb-4 rounded-full bg-[#393939] flex items-center justify-center">
              <img
                src={item.icon}
                alt=""
                className="w-6 h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 object-contain"
              />
            </div>

            {/* Text Content */}
            <div className="flex-1">
              <h3 className="text-lg md:text-base lg:text-lg font-medium mb-2 md:mb-2 text-[#FFFFFF]">
                {item.title}
              </h3>
              <p className="text-sm md:text-xs lg:text-sm text-[#8E8E8E] leading-relaxed">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Description */}
      <p className=" font-light text-sm md:text-sm lg:text-lg text-[#DDDDDD] leading-relaxed mb-8">
        In order to provide an effective immersive experience, spatial computing
        involves seamless integration with the hardware controllers, tracking
        mechanisms and other devices with very low latency and intuitive
        interaction design.
        <span className="font-bold ml-1">Spatial Grid's SpatialOS</span> enables
        seamless integration with major XR platforms and delivers consistent,
        high-performance experiences across multiple form factors.
      </p>
    </div>
  );
};

const AccordionCard = ({ title, desc }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-[#212121] rounded-[10px] overflow-hidden border border-[#424242]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex justify-between items-center"
      >
        <span className="text-white font-semibold">{title}</span>
        <svg
          className={`w-5 h-5 text-white transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <p className="text-[#CFCCCC] font-normal text-sm">{desc}</p>
        </div>
      )}
    </div>
  );
};
export const DesignedForDevelopersContent = () => {
  const featureData = [
    {
      title: 'Convenience',
      desc: 'APIs and Integration Patterns',
    },
    {
      title: 'Security & Reliability',
      desc: 'Consistent, predictable behavior across devices',
    },
    {
      title: 'Flexibility',
      desc: 'Build once, deploy across multiple form factors',
    },
    {
      title: 'Responsiveness',
      desc: 'Optimized performance & low-latency interaction',
    },
  ];

  return (
    <div className="relative w-full flex justify-center py-0 md:py-24 ">
      {/* CARD WIDTH CONTROLLER */}
      <div className="relative max-w-[1500px] w-full py-6 md:py-10 ">
        {/* HEADER - Centered on mobile */}
        <div className="text-left md:text-center py-6 md:py-10 md:absolute md:top-0 md:left-1/2 md:-translate-x-1/2 z-[20]">
          <h2 className="text-xl md:text-5xl font-semibold md:font-light text-white mb-2">
            Designed for Developers
          </h2>
          <p className="text-[#CFCCCC] text-base md:text-2xl  md:text-nowrap">
            SpatialOS is built to make it easier & faster to build immersive
            applications.
          </p>
        </div>

        {/* MOBILE VIEW - matches image exactly */}
        <div className="md:hidden  space-y-12">
          {/* Tags - Carousel style */}
          <div className="relative overflow-hidden">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar">
              <div className="border-[2px] border-[#D9D9D9] rounded-full px-4 font-normal py-1.5 text-sm text-[#D9D9D9] whitespace-nowrap flex-shrink-0">
                Diverse hardware types
              </div>
              <div className="border-[2px] border-[#D9D9D9] rounded-full px-4 font-normal py-1.5 text-sm text-[#D9D9D9] whitespace-nowrap flex-shrink-0">
                Diverse hardware Controllers and inputs
              </div>
              <div className="border-[2px] border-[#D9D9D9] rounded-full px-4 font-normal py-1.5 text-sm text-[#D9D9D9] whitespace-nowrap flex-shrink-0">
                Device driver complexity
              </div>
              <div className="border-[2px] border-[#D9D9D9] rounded-full px-4 font-normal py-1.5 text-sm text-[#D9D9D9] whitespace-nowrap flex-shrink-0">
                Adaptive hardware integration
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative flex justify-center my-8">
            <img
              src={imagePath?.MRGlassesWeb}
              alt="Headset"
              className="relative z-10 w-full max-w-[300px] object-contain"
            />
          </div>

          {/* Accordion cards */}
          <div className="space-y-4">
            {featureData.map((feature, index) => (
              <AccordionCard
                key={index}
                title={feature.title}
                desc={feature.desc}
              />
            ))}
          </div>
          <div className="relative h-[2px] w-full mt-44">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#ee2b2a] to-transparent opacity-50"></div>
          </div>
        </div>

        {/* DESKTOP/TABLET VIEW - original layout */}
        <div className="hidden md:block pt-32">
          <div className="flex justify-center">
            <div className="grid grid-cols-12 gap-6 max-w-[1280px] w-full">
              {/* LEFT */}
              <div className="col-span-4 pt-4">
                <h3 className="text-[18px] font-semibold  mb-6 text-white uppercase">
                  Abstracts
                </h3>

                <div className="flex flex-col gap-3">
                  {[
                    'Diverse hardware types',
                    'Diverse hardware controllers and inputs',
                    'Device driver complexity',
                    'Adaptive hardware integration',
                  ].map((item) => (
                    <div
                      key={item}
                      className="border-2 border-[#D9D9D9] rounded-full px-4 py-1.5 text-[14px] text-[#D9D9D9] w-fit"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT */}
              <div className="col-span-8 grid grid-cols-2 gap-x-6 gap-y-4">
                {/* IMAGE */}
                <div className="relative flex justify-center items-center">
                  <img
                    src={imagePath?.MRGlassesWeb}
                    alt="Headset"
                    className="relative z-10 right-10 top-3 w-full max-w-[420px] object-contain"
                  />
                </div>

                {/* CARDS */}
                <div className="space-y-4 mt-16">
                  <FeatureCard
                    title="Convenience"
                    desc="APIs and Integration Patterns"
                  />
                  <FeatureCard
                    title="Security & Reliability"
                    desc="Consistent, predictable behavior across devices"
                  />
                </div>

                <div className="flex justify-end">
                  <FeatureCard
                    title="Flexibility"
                    desc="Build once, deploy across multiple form factors"
                  />
                </div>

                <FeatureCard
                  title="Responsiveness"
                  desc="Optimized performance & low-latency interaction"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ title, desc }) => (
  <div
    className="bg-[#F0F0F0] p-6 md:p-8 min-h-[130px] md:min-h-[140px] lg:min-h-[160px] w-auto flex flex-col justify-between"
    style={{
      clipPath: `
    polygon(
      0 18px,
      18px 0,
      calc(100% - 18px) 0,
      100% 18px,
      100% calc(100% - 18px),
      calc(100% - 18px) 100%,
      18px 100%,
      0 calc(100% - 18px)
    )
  `,
    }}
  >
    <p className="text-sm md:text-base text-[#000000] font-normal mb-4 md:mb-6">
      {title}
    </p>
    <h3 className="text-lg md:text-xl font-bold text-[#000000] leading-tight">
      {desc}
    </h3>
  </div>
);

export const GlassesFeatureCard = ({
  title,
  desc,
  bg = '#111111',
  curvePosition = 'top-left',
  iconSrc,
}) => {
  const clipPaths = {
    'top-left':
      'path("M 15,30 C 7,30 0,37 0,45 L 0,85 C 0,93 7,100 15,100 L 335,100 C 343,100 350,93 350,85 L 350,15 C 350,7 343,0 335,0 L 115,0 C 110,0 108,3 105,10 C 102,15 100,30 85,30 Z")',
    'bottom-right':
      'path("M 15,0 C 7,0 0,7 0,15 L 0,85 C 0,93 7,100 15,100 L 235,100 C 240,100 242,97 245,90 C 248,85 250,70 265,70 L 335,70 C 343,70 350,63 350,55 L 350,15 C 350,7 343,0 335,0 Z")',
  };

  return (
    <div className="inline-block leading-none">
      <div
        className="text-white flex items-center gap-4
                   min-w-[350px] max-w-[374px] h-[100px]"
        style={{
          background: bg,
          clipPath: clipPaths[curvePosition],
          paddingLeft: '12px',
          paddingRight: '28px',
          paddingTop: curvePosition === 'top-left' ? '36px' : '8px',
          paddingBottom: curvePosition === 'bottom-right' ? '36px' : '8px',
        }}
      >
        {/* Icon */}
        <div className="flex-shrink-0 flex items-center justify-center">
          <img
            src={iconSrc}
            alt="icon"
            width="45"
            height="45"
            className="w-[32px] h-[32px] object-contain"
          />
        </div>

        {/* Text */}
        <div className="flex flex-col justify-center">
          <p className="text-[14px] leading-tight tracking-tight">
            <span className="font-semibold text-white">{title}</span>{' '}
            <span className="text-[#B3B3B3] semibold">{desc}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export const GlassesFeatureCardRightCurve = ({
  title,
  desc,
  bg = '#111111',
}) => {
  return (
    <div className="inline-block leading-none">
      <div
        className="text-white p-3 pr-8 flex items-center gap-4
                   min-w-[350px] max-w-[380px] h-[82px]"
        style={{
          background: bg,
          clipPath:
            'path("M 15,0 C 7,0 0,7 0,15 L 0,67 C 0,75 7,82 15,82 L 265,82 C 270,82 272,79 275,74 C 278,69 280,57 295,57 C 303,57 310,50 310,42 L 310,15 C 310,7 303,0 295,0 Z")',
        }}
      >
        {/* Icon */}
        <div className="flex-shrink-0 ml-1">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <circle
              cx="12"
              cy="5"
              r="2.5"
              fill="currentColor"
              fillOpacity="0.2"
            />
            <path
              d="M17 10c0-1.5-1.5-2.5-3-2.5h-4c-1.5 0-3 1-3 2.5v1h10v-1z"
              fill="currentColor"
              fillOpacity="0.2"
            />
          </svg>
        </div>

        {/* Text */}
        <div className="flex flex-col justify-center pt-4">
          <p className="text-[17px] leading-tight tracking-tight">
            <span className="font-bold text-black">{title}</span>{' '}
            <span className="text-[#999999] font-medium">{desc}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export const BottomFeatureContent = () => {
  return (
    <div className="flex flex-col gap-16 w-full text-left">
      {/* Header Section */}
      <div className="flex flex-col gap-4 border-b border-white/10 pb-6">
        <h1 className="text-white text-5xl font-medium tracking-tight">
          Designed for Users
        </h1>
        <p className="text-zinc-400 max-w-4xl text-lg">
          For end users, SpatialOS is about making immersive experiences feel
          natural, safe, and seamless. SpatialOS applications can offer:
        </p>
      </div>

      {/* Grid Section */}
      <div className="grid grid-cols-12 gap-6 items-stretch">
        {/* Large Card (Multiple Tracking) */}
        <div className="col-span-8 rounded-[2.5rem] min-h-[400px] relative overflow-hidden flex">
          {/* Background Image */}
          <img
            src={imagePath?.UserCard1} // replace with your image path
            alt="Multiple tracking"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Content */}
          <div className="relative z-10 p-12 flex flex-col justify-end w-full">
            <h2 className="text-3xl font-bold text-black mb-4">
              Multiple tracking mechanisms
            </h2>
            <ul className="text-black space-y-1 text-lg font-medium">
              <li>• Object tracking</li>
              <li>• Image tracking</li>
              <li>• Location-based tracking</li>
              <li>• Other spatial anchors</li>
            </ul>
          </div>
        </div>

        {/* Intelligence Card (Top Right) */}
        <div className="col-span-4 rounded-[2.5rem] min-h-[280px] relative overflow-hidden flex">
          {/* Background Image */}
          <img
            src={imagePath?.UserCard2} // replace with your image path
            alt="Intelligence across worlds"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Content */}
          <div className="relative z-10 p-10 flex flex-col w-full">
            <div className="mt-auto">
              <h2
                className="text-2xl font-bold text-black
               leading-tight mb-2"
              >
                Intelligence across physical and digital worlds
              </h2>
              <ul className="text-black space-y-2 font-medium">
                <li>
                  • Smart interactions with digital and real-world objects
                </li>
                <li>• Context-aware prompts and guidance</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Situational Awareness (Bottom Left) */}
        <div className="col-span-4 rounded-[2.5rem] min-h-[280px] relative overflow-hidden flex">
          {/* Background Image */}
          <img
            src={imagePath?.UserCard3} // replace with your image
            alt="Situational awareness & safety"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Content */}
          <div className="relative z-10 p-10 flex flex-col w-full">
            <div className="mt-auto">
              <h2 className="text-2xl font-bold text-black mb-2">
                Situational awareness & safety
              </h2>
              <ul className="text-black space-y-2 font-medium">
                <li>• Spatial understanding to avoid unsafe interactions</li>
                <li>• System-level checks and boundaries</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Image Card (Center Red) */}
        <div className="col-span-4 bg-red-600 rounded-[2.5rem] p-8 flex flex-col relative overflow-hidden group cursor-pointer">
          <div className="flex justify-between items-center text-white text-xl font-medium">
            <span>Lets Talk</span>
            <span className="text-2xl">↗</span>
          </div>
          <div className="flex-1 flex items-center justify-center pt-4">
            {/* Replace with your actual glasses image */}
            <div className="w-full h-40 bg-zinc-900/20 rounded-xl" />
          </div>
        </div>

        {/* Usage Policies (Bottom Right) */}
        <div className="col-span-4 rounded-[2.5rem] min-h-[280px] relative overflow-hidden flex">
          {/* Background Image */}
          <img
            src={imagePath?.UserCard3} // replace with your image
            alt="Usage policies"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Content */}
          <div className="relative z-10 p-10 flex flex-col w-full">
            <div className="mt-auto">
              <h2 className="text-2xl font-bold text-black mb-2">
                Usage policies for ethical, pleasant experiences
              </h2>
              <ul className="text-black space-y-2 font-medium">
                <li>• Guardrails for how, when, and where content appears</li>
                <li>
                  • Controls aligned with moral and organizational standards
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FolderCard = ({ children }) => {
  return (
    <div className="w-full relative min-h-[600px]">
      {/* Background SVG Layer */}
      <div className="absolute inset-0 z-0">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1000 1000"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M 0 0 
               H 500 
               C 510 0 515 5 520 25 
               L 530 65 
               C 535 85 545 90 560 90 
               H 1000 
               V 1000 
               H 0 
               Z"
            fill="black"
          />
        </svg>
      </div>

      {/* Content Layer */}
      <div className="relative z-10 w-full max-w-[1356px] mx-auto px-8 pt-24 ">
        {children}
      </div>
    </div>
  );
};

export const GridBackground = ({ size = '50px' }) => {
  return (
    <div
      className="absolute inset-0 z-0 pointer-events-none"
      style={{
        backgroundImage: `
          linear-gradient(to right, #e5e7eb 1px, transparent 1px),
          linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
        `,
        backgroundSize: `${size} ${size}`,
        maskImage: `
          linear-gradient(to right, transparent 0%, black 40%, black 60%, transparent 100%),
          linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)
        `,
        WebkitMaskImage: `
          linear-gradient(to right, transparent 0%, black 40%, black 60%, transparent 100%),
          linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)
        `,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      }}
    />
  );
};
